import axiosInstance, { endpoints } from 'src/utils/axios';

// hooks
import useGetData from 'src/hooks/use-get-data';
// types
import {
  IProductItem,
  ICatalogShopViewFilters,
  IFetchBaseParams,
  APIListResponse,
  IListFilters,
  IProducerItem,
  ICartProductsItem,
  IAddToCart,
  IOrderPrepayment,
  ICurrentCartOrder,
  IConfirmOrder,
  ICustomerOrderItem,
  ICountries,
  IClientOrderDetails,
  IWineTypeItem,
  IClientActiveOverlayData,
  IClientDetailsData,
  IAgentAssociatedClients,
  ISelectionItem,
} from 'src/types';
import { isUserAgent, isLoggedIn } from 'src/utils/check-user-is-admin';

export function useGetProducers(params?: IListFilters<IFetchBaseParams>) {
  return useGetData<IListFilters<IFetchBaseParams>, APIListResponse<IProducerItem[]>>({
    // eslint-disable-next-line no-nested-ternary
    endpoint: isUserAgent()
      ? params?.clientId
        ? endpoints.customer.producers.root
        : null
      : endpoints.customer.producers.root,
    params,
  });
}
// /customer/products/pricing
export function useGetProducts(params?: Partial<ICatalogShopViewFilters>) {
  return useGetData<
    Partial<ICatalogShopViewFilters>,
    APIListResponse<IProductItem[]> & {
      low_price: number;
      high_price: number;
      products: IProductItem[];
    }
  >({
    endpoint: endpoints.customer.products.root,
    params,
  });
}

export function useGetSelections() {
  return useGetData<Record<string, never>, ISelectionItem[]>({
    endpoint: endpoints.customer.selections,
  });
}

export function useGetClientOrderList(params?: Partial<IFetchBaseParams>, enabled: boolean = true) {
  return useGetData<Partial<IFetchBaseParams>, APIListResponse<ICustomerOrderItem[]>>({
    endpoint: enabled ? endpoints.customer.client_orders.root : null,
    params,
  });
}

export function useGetProductsPricing() {
  return useGetData<
    {},
    {
      low_price: number;
      high_price: number;
    }
  >({
    endpoint: endpoints.customer.products.pricing,
  });
}

export function useGetWineTypes(params?: Partial<IFetchBaseParams>) {
  return useGetData<Partial<IFetchBaseParams>, IWineTypeItem[]>({
    endpoint: endpoints.customer.products.wine_types,
    params,
  });
}

export function useGetAgentAssociatedClients(params?: Partial<IFetchBaseParams>) {
  return useGetData<Partial<IFetchBaseParams>, IAgentAssociatedClients[]>({
    endpoint: isUserAgent() ? endpoints.admin.agent_associated_clients : null,
    params,
  });
}

export function useGetGrapeTypes(params?: Partial<IFetchBaseParams>) {
  return useGetData<Partial<IFetchBaseParams>, { grape_types: { name: string; id: string }[] }>({
    endpoint: endpoints.customer.products.grape_types,
    params,
  });
}

export function useGetCountries(params?: Partial<IFetchBaseParams>) {
  return useGetData<Partial<IFetchBaseParams>, ICountries>({
    // eslint-disable-next-line no-nested-ternary
    endpoint: isUserAgent()
      ? params?.clientId
        ? endpoints.customer.producers.countries
        : null
      : endpoints.customer.producers.countries,
    params,
  });
}

export function useGetCartProducts(params?: Partial<IFetchBaseParams>) {
  // Determine endpoint based on authentication and user type
  let endpoint = null;

  if (isLoggedIn()) {
    if (isUserAgent()) {
      // For agents, require clientId
      endpoint = params?.clientId ? endpoints.customer.cart_products.root : null;
    } else {
      // For regular customers
      endpoint = endpoints.customer.cart_products.root;
    }
  }

  return useGetData<Partial<IFetchBaseParams>, ICartProductsItem>({
    endpoint,
    params,
  });
}

export function useGetCurrentOrder(params?: Partial<IFetchBaseParams>) {
  const isCartEmpty = params?.isCartEmpty || false;

  // Only make API call if user is logged in and cart is not empty
  let endpoint = null;
  if (isLoggedIn() && !isCartEmpty) {
    endpoint = endpoints.customer.client_orders.current_order;
  }

  return useGetData<Partial<IFetchBaseParams>, ICurrentCartOrder>({
    endpoint,
    params: { clientId: params?.clientId },
  });
}

export function useGetClientOrder({ order_code }: IFetchBaseParams, enabled: boolean = true) {
  return useGetData<Partial<IFetchBaseParams>, IClientOrderDetails>({
    // Null endpoint = SWR skip (same pattern as useGetClientOrderList): callers
    // that mount before an order code exists must not fire /client_orders//new.
    endpoint:
      enabled && order_code ? endpoints.customer.client_orders.details(order_code as string) : null,
  });
}
// /customer/payment-status
// implement an API for payment status updation in client order
export async function updateClientOrderPaymentFields({
  orderId,
  stripePaymentIntentId,
}: {
  orderId: string | null;
  stripePaymentIntentId: string | null;
}) {
  const URL = `${endpoints.customer.payment_status}`;
  await axiosInstance.put(URL, { orderId, stripePaymentIntentId });
}

export async function addToCart(data: IAddToCart) {
  const URL = `${endpoints.customer.cart_products.root}`;
  await axiosInstance.post(URL, data);
}

export async function deleteFromCart(sku: string, clientId?: string | number) {
  const URL = isUserAgent()
    ? `${endpoints.customer.cart_products.root}/${sku}/${clientId}`
    : `${endpoints.customer.cart_products.root}/${sku}`;
  await axiosInstance.delete(URL);
}

export async function updateOrderPrepayment(data: IOrderPrepayment) {
  const URL = `${endpoints.customer.client_orders.current}`;
  await axiosInstance.put(URL, data);
}

export async function updateCurrentOrderAddress(data: Partial<ICurrentCartOrder>) {
  const URL = `${endpoints.customer.client_orders.current}`;
  await axiosInstance.put(URL, data);
}

export async function confirmOrder(payload: IConfirmOrder) {
  const URL = `${endpoints.customer.client_orders.current}`;
  const {
    data: { data: res },
  } = await axiosInstance.put(URL, { ...payload });
  return res;
}
export function useGetClientActiveOverlay() {
  return useGetData<Partial<IFetchBaseParams>, IClientActiveOverlayData>({
    endpoint: endpoints.customer.overlay.active,
  });
}
export function useGetClientFallbackOverlay() {
  return useGetData<Partial<IFetchBaseParams>, IClientActiveOverlayData>({
    endpoint: endpoints.customer.overlay.fallback,
  });
}
export async function MarkOverlaySeen(overlay_id: string) {
  const URL = `${endpoints.customer.overlay.mark_seen}/${overlay_id}/seen`;
  await axiosInstance.post(URL);
}
export function useGetClientDetails() {
  return useGetData<Partial<IFetchBaseParams>, IClientDetailsData>({
    endpoint: endpoints.customer.overlay.client_details,
  });
}

export async function getCustomerOrderPaymentUrl(orderCode: string) {
  const queryString = new URLSearchParams({
    orderCode: orderCode?.toString() ?? '',
  }).toString();
  const URL = `${endpoints.customer.cart_products.checkout}?${queryString}`;
  const res = await axiosInstance.post(URL);
  return res;
}
